import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { parseBookingQuery } from "../common/url/bookingQuery";
import routes from "../../../../routes/routes";

/**
 * Custom hook to guard against missing required query parameters.
 * Redirects to appropriate step if required params are missing.
 * 
 * @remarks
 * - Validates locationId and serviceId presence
 * - Redirects to location selection if locationId is missing
 * - Redirects to service selection if serviceId is missing
 * - Uses replace navigation to avoid history pollution
 * 
 * @example
 * ```tsx
 * function SummaryPage() {
 *   useQueryParamGuard();
 *   // Will redirect if params are missing
 *   return <Content />;
 * }
 * ```
 */
export function useQueryParamGuard(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const query = parseBookingQuery(location.search);

    if (!query.locationId) {
      navigate(
        { pathname: routes.keyHandoverLocationSelectionPath, search: location.search },
        { replace: true }
      );
      return;
    }

    if (!query.serviceId) {
      navigate(
        { pathname: routes.keyHandoverServiceSelectionPath, search: location.search },
        { replace: true }
      );
    }
  }, [location.search, navigate]);
}
